/**
 * What a poll is allowed to be, and what a given person is allowed to see of
 * it. Pure, and shared, because three tiers have to agree: the callable that
 * validates a new one, the rules that decide who may read the votes, and the
 * screen that draws the result.
 */
import type { PollDoc, PollOption, PollVoteDoc } from './types';

/** Fewer than two is not a question, and more than ten is a survey. */
export const MIN_POLL_OPTIONS = 2;
export const MAX_POLL_OPTIONS = 10;
export const MAX_POLL_QUESTION = 200;
export const MAX_POLL_LABEL = 80;

/**
 * Why this poll cannot be saved, or null when it can.
 *
 * Returns the reason rather than a boolean so the person typing is told which
 * part is wrong, instead of a button that stays dim without saying why.
 */
export function pollProblem(question: string, labels: string[]): string | null {
  const asked = question.trim();
  if (!asked) return 'A poll needs a question.';
  if (asked.length > MAX_POLL_QUESTION) {
    return `That question is longer than ${MAX_POLL_QUESTION} characters.`;
  }

  const filled = labels.map((label) => label.trim()).filter(Boolean);
  if (filled.length < MIN_POLL_OPTIONS) return 'A poll needs at least two options.';
  if (filled.length > MAX_POLL_OPTIONS) {
    return `A poll takes at most ${MAX_POLL_OPTIONS} options.`;
  }
  if (filled.some((label) => label.length > MAX_POLL_LABEL)) {
    return `An option is longer than ${MAX_POLL_LABEL} characters.`;
  }

  const seen = new Set(filled.map((label) => label.toLowerCase()));
  if (seen.size !== filled.length) return 'Two options say the same thing.';

  return null;
}

/**
 * Whether this person may see who voted for what.
 *
 * Voting is what buys it, which is the point: a poll nobody has answered
 * should not be swayed by watching it fill up. The author is exempt because
 * they are running it and may need to chase people who have not answered.
 *
 * The rules enforce the same sentence on the votes subcollection. This copy
 * is so the screen does not ask for documents it is going to be refused.
 */
export function canSeeVotes(
  poll: { closedAt?: unknown } | undefined,
  authorUid: string,
  uid: string,
  hasVoted: boolean,
): boolean {
  return uid === authorUid || hasVoted || Boolean(poll?.closedAt);
}

export interface PollTally {
  option: PollOption;
  /** Uids, in no particular order; the screen decides how many to draw. */
  voters: string[];
}

/**
 * The count against each option.
 *
 * Votes naming an option that has since been removed are dropped rather than
 * counted into a total nobody can point at.
 */
export function tallyPoll(
  poll: Pick<PollDoc, 'options'>,
  votes: Record<string, Pick<PollVoteDoc, 'optionIds'>>,
): PollTally[] {
  const byOption = new Map<string, string[]>(poll.options.map((each) => [each.id, []]));

  for (const [uid, vote] of Object.entries(votes)) {
    // A person counts once per option however many times their document names
    // it, so a duplicated id cannot inflate a result.
    for (const optionId of new Set(vote.optionIds ?? [])) {
      byOption.get(optionId)?.push(uid);
    }
  }

  return poll.options.map((option) => ({
    option,
    voters: byOption.get(option.id) ?? [],
  }));
}

/**
 * People, not ticks. With several answers allowed a person can be in three
 * columns at once, and "12 voted" out of a family of eleven reads as a fault.
 */
export function votersOf(votes: Record<string, unknown>): number {
  return Object.keys(votes).length;
}

/** What this person has chosen, after a tap on `optionId`. */
export function nextChoice(
  current: string[],
  optionId: string,
  multi: boolean,
): string[] {
  if (!multi) {
    // Tapping the one already chosen takes it back, so a vote can be undone
    // and not only moved.
    return current.includes(optionId) ? [] : [optionId];
  }
  return current.includes(optionId)
    ? current.filter((each) => each !== optionId)
    : [...current, optionId];
}
